import { EditOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Modal, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { timeToMinutes } from "~/lib/localize.js";
import { AddButton, EditButton } from "../buttons/index.js";
import { DateFormat } from "../date/DateFormat.jsx";
import { FlightMotorTime } from "./FlightMotorTime.jsx";
import { TacometerInput } from "./TacometerInput.jsx";

export const FlightTacometer = ({
  motor_format,
  flight,
  saveFlight,
  skipTitle,
  readOnly,
  active,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [beginMinutes, setBeginMinutes] = useState(0);
  const [form] = Form.useForm();

  const [loadingMotorData, setLoadingMotorData] = useState(false);
  const [motorTimeItems, SetMotorTimeItems] = useState([]);
  const shouldLoadMotorData = active === undefined ? isModalOpen : active;

  const hasMotorHours = flight.motor_minutes > 0;
  const vluchtDuur =
    flight.blocktime > 0
      ? flight.blocktime
      : flight.vluchtduur > 0
        ? flight.vluchtduur
        : 0;
  const isMotorGlider =
    flight.start_methode?.startsWith("tmg") ||
    flight.start_methode?.startsWith("sep");
  const showMotorSuggestion =
    isMotorGlider && !hasMotorHours && beginMinutes > 0 && vluchtDuur > 0;

  const createMotorTimeItems = (data) => {
    const items = [];
    const currentDate = flight.datum;
    const currentFlightStartMinutes = timeToMinutes(flight.start_tijd);

    // 1. Add flights from current date (sorted by start time)
    const todayFlights = (data.flights || [])
      .filter((f) => {
        if (f.uuid === flight.uuid) return false; // Exclude current flight
        if (flight.start_tijd) {
          const flightStartMinutes = timeToMinutes(f.start_tijd);
          // Include only flights that started before the current flight
          return (
            f.datum === currentDate &&
            flightStartMinutes > 0 &&
            flightStartMinutes <= currentFlightStartMinutes
          );
        }
        return f.datum === currentDate;
      })
      .sort(
        (a, b) => timeToMinutes(b.start_tijd) - timeToMinutes(a.start_tijd),
      );

    todayFlights.forEach((f) => {
      items.push({
        type: "flight",
        datum: f.datum,
        start_tijd: f.start_tijd,
        motor_end: f.motor_end,
        motor_minutes: f.motor_minutes,
        id: f.id,
      });
    });

    // 2. Add inspections from current date
    const todayInspections = (data.inspections || []).filter(
      (i) => i.datum === currentDate,
    );

    todayInspections.forEach((i) => {
      if (i.total_motor_minutes > 0) {
        items.push({
          type: "inspection",
          datum: i.datum,
          start_tijd: null,
          motor_end: i.total_motor_minutes,
          motor_minutes: 0,
          id: `inspection-${i.id}`,
        });
      }
    });

    // 3. Add flights from previous days
    const previousFlights = (data.flights || []).filter(
      (f) => f.datum !== currentDate,
    );

    previousFlights.forEach((f) => {
      items.push({
        type: "flight",
        datum: f.datum,
        start_tijd: f.start_tijd,
        motor_end: f.motor_end,
        motor_minutes: f.motor_minutes,
        id: f.id,
      });
    });

    SetMotorTimeItems(items);

    // Set begin motor time from first item that has motor time
    const firstItemWithMotorTime = items.find((item) => item.motor_end > 0);
    if (firstItemWithMotorTime && !flight.motor_end) {
      setBeginMinutes(firstItemWithMotorTime.motor_end);
      form.setFieldValue("begin_minutes", firstItemWithMotorTime.motor_end);
      form.setFieldValue("motor_end", firstItemWithMotorTime.motor_end);
    }
  };

  useEffect(() => {
    if (!shouldLoadMotorData || !flight?.kist_id) return undefined;

    const controller = new AbortController();
    setBeginMinutes(
      flight.motor_end > 0 && flight.motor_minutes > 0
        ? flight.motor_end - flight.motor_minutes
        : 0,
    );
    setLoadingMotorData(true);
    api
      .post(
        "materiaal/kist_motor_minutes.json",
        { kist_id: flight.kist_id, date: flight.datum },
        true,
        controller.signal,
      )
      .then((data) => {
        if (data && !controller.signal.aborted) createMotorTimeItems(data);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingMotorData(false);
      });

    return () => controller.abort();
  }, [
    shouldLoadMotorData,
    flight?.uuid,
    flight?.kist_id,
    flight?.datum,
    flight?.start_tijd,
  ]);

  useEffect(() => {
    if (active === false) setIsModalOpen(false);
  }, [active]);

  const handleSetMotorTime = (item) => {
    setBeginMinutes(item.motor_end);
    form.setFieldValue("begin_minutes", item.motor_end);

    // Recalculate used motor time if end motor time is already set
    const currentEndTime = form.getFieldValue("motor_end");
    if (currentEndTime > 0) {
      const calculatedUsedTime = currentEndTime - item.motor_end;
      form.setFieldValue("motor_minutes", Math.max(0, calculatedUsedTime));
    }
  };

  const handleEndMotorChange = (value) => {
    const beginTime = form.getFieldValue("begin_minutes");

    // Ensure end time is not less than begin time
    if (beginTime > 0 && value < beginTime) {
      // Don't update if end time would be less than begin time
      return;
    }

    form.setFieldValue("motor_end", value);

    // Calculate used motor time if begin time is set
    if (beginTime > 0 && value > 0) {
      const calculatedUsedTime = value - beginTime;
      form.setFieldValue("motor_minutes", Math.max(0, calculatedUsedTime));
    }
  };

  const handleUsedMotorChange = (value) => {
    form.setFieldValue("motor_minutes", value);

    // Calculate end motor time if begin time is set
    const beginTime = form.getFieldValue("begin_minutes");
    if (beginTime >= 0 && value >= 0) {
      const calculatedEndTime = beginTime + value;
      form.setFieldValue("motor_end", calculatedEndTime);
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);

    // Derive begin minutes from flight data if motor_end is set
    let derivedBeginMinutes = beginMinutes;
    if (flight.motor_end > 0 && flight.motor_minutes > 0) {
      derivedBeginMinutes = flight.motor_end - flight.motor_minutes;
      setBeginMinutes(derivedBeginMinutes);
    }
    const hasMotorSuggestion =
      !hasMotorHours &&
      derivedBeginMinutes > 0 &&
      vluchtDuur > 0 &&
      isMotorGlider;
    const suggestedMotorMinutes = hasMotorSuggestion
      ? vluchtDuur
      : flight.motor_minutes || 0;

    // Set initial form values
    form.setFieldsValue({
      motor_end: flight.motor_end ||
        (hasMotorSuggestion
          ? derivedBeginMinutes + suggestedMotorMinutes
          : 0),
      motor_minutes: suggestedMotorMinutes,
      begin_minutes: derivedBeginMinutes,
    });
  };

  const handleAcceptSuggestion = async () => {
    const suggestion = {
      motor_minutes: vluchtDuur,
      motor_end: beginMinutes + vluchtDuur,
    };
    form.setFieldsValue(suggestion);
    await saveFlight?.(suggestion);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (saveFlight) {
        saveFlight({
          motor_end: values.motor_end,
          motor_minutes: values.motor_minutes,
        });
      }
      setIsModalOpen(false);
    } catch (error) {
      console.log("Validation failed:", error);
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    form.setFieldsValue({
      motor_end: flight.motor_end || 0,
      motor_minutes: flight.motor_minutes || 0,
      begin_minutes: beginMinutes,
    });
    setIsModalOpen(false);
  };

  const motor_end = Form.useWatch("motor_end", form);
  const begin_minutes = Form.useWatch("begin_minutes", form);

  return (
    <>
      <div className="row">
        {!skipTitle && <p style={{ width: 80 }}>{$t("Motor uren")}:</p>}

        {hasMotorHours && (
          <FlightMotorTime motorMinutes={flight.motor_minutes} />
        )}

        {!hasMotorHours && !showMotorSuggestion && (
          <AddButton
            size="medium"
            type={isMotorGlider ? "primary" : "default"}
            onClick={handleModalOpen}
            disabled={readOnly}
          />
        )}

        {showMotorSuggestion && (
          <div className="column" style={{ alignItems: "flex-start", gap: 4 }}>
            <p className="gray row" style={{ gap: 4 }}>
              {$t("Suggestie")}:
              <FlightMotorTime motorMinutes={vluchtDuur} />
            </p>
            <div className="row" style={{ gap: 8, alignItems: "center" }}>
              <Button
                type="primary"
                size="small"
                onClick={handleAcceptSuggestion}
                disabled={readOnly}
              >
                {$t("Accepteer")}
              </Button>
              <Button
                type="text"
                size="small"
                onClick={handleModalOpen}
                disabled={readOnly}
                icon={<EditOutlined />}
              >
                {$t("Andere tijd")}
              </Button>
            </div>
          </div>
        )}

        {hasMotorHours && (
          <EditButton
            size="small"
            onClick={handleModalOpen}
            style={{ marginLeft: 8 }}
            disabled={readOnly}
          />
        )}
      </div>

      <Modal
        title={$t("Motor uren bewerken")}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={$t("Opslaan")}
        cancelText={$t("Annuleer")}
        width={450}
      >
        <Form
          form={form}
          layout="vertical"
          labelCol={{ span: 12 }}
          wrapperCol={{ span: 24 }}
          initialValues={{
            motor_end: flight.motor_end || 0,
            motor_minutes: flight.motor_minutes || 0,
            begin_minutes: beginMinutes,
          }}
        >

          <Form.Item
            label={$t("Eindstand motor")}
            name="motor_end"
            required={false}
            rules={[
              {
                validator: (_, value) => {
                  const beginTime = form.getFieldValue("begin_minutes");
                  if (beginTime > 0 && value < beginTime) {
                    return Promise.reject(
                      new Error(
                        $t(
                          "Eindstand motor moet groter zijn aan beginstand motor",
                        ),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TacometerInput
              motor_format={motor_format}
              readOnly={false}
              value={motor_end}
              onChange={handleEndMotorChange}
            />
          </Form.Item>
          
          <Form.Item
            label={$t("Gebruikte motor tijd")}
            name="motor_minutes"
            required={false}
            rules={[
              {
                validator: (_, value) => {
                  if (vluchtDuur > 0 && value > vluchtDuur) {
                    return Promise.reject(
                      new Error(
                        $t(
                          "Gebruikte motor tijd mag niet groter zijn dan de vluchtduur",
                        ),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TacometerInput
              motor_format={motor_format}
              readOnly={false}
              value={form.getFieldValue("motor_minutes")}
              onChange={handleUsedMotorChange}
            />
          </Form.Item>


          <Form.Item label={$t("Beginstand motor")} name="begin_minutes">
            <TacometerInput
              motor_format={motor_format}
              value={begin_minutes}
              onChange={(value) => {
                setBeginMinutes(value);
                form.setFieldValue("begin_minutes", value);
              }}
            />
          </Form.Item>

          {beginMinutes !== null && beginMinutes !== begin_minutes && (
            <Alert
              style={{ marginTop: 12 }}
              type="error"
              message={$t(
                "Let op, de ingevulde begintijd is gewijzigd. Check of dit klopt.",
              )}
            />
          )}
        </Form>

        <b className="title label">{$t("Laatste motor tijd")} ({$t("voor beginstand")})</b>

        {loadingMotorData && (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin size="large" />
          </div>
        )}

        {motorTimeItems.length > 0 && (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {motorTimeItems.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom:
                    index < motorTimeItems.length - 1
                      ? "1px solid #f0f0f0"
                      : "none",
                }}
              >
                <div className="row" style={{ gap: 6 }}>
                  <Button
                    size="small"
                    disabled={!item.motor_end || item.motor_end === beginMinutes}
                    onClick={() => handleSetMotorTime(item)}
                  >
                    {$t("Zet tijd")}
                  </Button>

                  <div style={{ width: 75 }}>
                    <DateFormat
                      date={item.datum}
                      options={{
                        day: "2-digit",
                        month: "short",
                        year: "2-digit",
                      }}
                    />
                  </div>

                  <div style={{ width: 80 }}>
                    {item.start_tijd && <span>{item.start_tijd}</span>}
                    {item.type === "inspection" && (
                      <span style={{ fontStyle: "italic" }}>
                        {$t("Inspectie")}
                      </span>
                    )}
                  </div>
                  <FlightMotorTime motorMinutes={item.motor_end} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loadingMotorData && motorTimeItems.length === 0 && (
          <p style={{ textAlign: "center", color: "#999", padding: 20 }}>
            {$t("Geen motor tijden gevonden")}
          </p>
        )}
      </Modal>
    </>
  );
};
